import type { CompetencyLevel } from '../../human-resources/personnel/personnelTypes';

/**
 * One survey — Learning & Development › Survey.
 *
 * Not the Human Resources survey questionnaire (`features/human-resources/survey`), which is
 * what a 201 file is answered against. The two share a word and nothing else.
 */
export interface Survey {
  id: string;

  title: string;

  /** The divisions it covers, with their LIVE names. EMPTY MEANS ALL — none was singled out. */
  orgUnits: SurveyTarget[];

  /** The positions it covers, with their LIVE titles. EMPTY MEANS ALL — none was singled out. */
  positions: SurveyTarget[];

  /**
   * How many have responded — the survey's live respondents, counted by the server, never stored.
   */
  respondentCount: number;
  /** Live responses already assessed — what the Development Plan lists. */
  assessedCount: number;

  /** Live questions on its live questionnaire. Zero: nothing to answer yet. */
  questionCount: number;

  /** Where the survey is in its run — numeric on the wire. Answered only while Started. */
  status: SurveyStatusValue;
  /** When it was FIRST started. Absent until then. */
  startedAt?: string | null;
  startedByName?: string | null;
  /** When it was ended. Absent unless Done. */
  endedAt?: string | null;
  endedByName?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** The backend's `SurveyStatus` — numeric on the wire, derived from the start and end stamps. */
export const SURVEY_STATUS = { NotStarted: 0, Started: 1, Done: 2 } as const;
export type SurveyStatusValue = (typeof SURVEY_STATUS)[keyof typeof SURVEY_STATUS];

export const SURVEY_STATUS_LABELS: Record<SurveyStatusValue, string> = {
  0: 'Not started',
  1: 'Started',
  2: 'Done',
};

/** One unit or position a survey covers. The name is resolved by the server on every read. */
export interface SurveyTarget {
  id: string;
  name: string;
}

/** The respondent count is never the caller's, so it is not here. An empty list means ALL. */
export interface SurveyRequest {
  title: string;
  orgUnitIds: string[];
  positionIds: string[];
}

/**
 * What kind of answer a questionnaire question takes — the product owner's list, in their words.
 *
 * **Numeric on the wire** (no string-enum converter is configured): an `as const` object of
 * numbers mirroring `SurveyQuestionType` on the backend. A member added there is added here in
 * the same change, or it falls through every arm.
 */
export const SURVEY_QUESTION_TYPE = {
  MultipleChoice: 0,
  ShortParagraph: 1,
  LongParagraph: 2,
  Scale: 3,
  Checkbox: 4,
  Dropdown: 5,
  Date: 6,
  Time: 7,
  /** WHO the response is for — the respondent's own record, or an employee picked on their behalf. Never a typed name. */
  Name: 8,
  /** An org unit, and a sector beneath it when it has any. */
  Division: 9,
} as const;

export type SurveyQuestionTypeValue = (typeof SURVEY_QUESTION_TYPE)[keyof typeof SURVEY_QUESTION_TYPE];

/** The type picker's wording, in the order the product owner listed them. */
export const SURVEY_QUESTION_TYPE_LABELS: { value: SurveyQuestionTypeValue; label: string }[] = [
  { value: SURVEY_QUESTION_TYPE.MultipleChoice, label: 'Multiple choice' },
  { value: SURVEY_QUESTION_TYPE.ShortParagraph, label: 'Short paragraph' },
  { value: SURVEY_QUESTION_TYPE.LongParagraph, label: 'Long paragraph' },
  { value: SURVEY_QUESTION_TYPE.Scale, label: 'Scale' },
  { value: SURVEY_QUESTION_TYPE.Checkbox, label: 'Checkbox' },
  { value: SURVEY_QUESTION_TYPE.Dropdown, label: 'Dropdown' },
  { value: SURVEY_QUESTION_TYPE.Date, label: 'Date' },
  { value: SURVEY_QUESTION_TYPE.Time, label: 'Time' },
  { value: SURVEY_QUESTION_TYPE.Name, label: 'Name' },
  { value: SURVEY_QUESTION_TYPE.Division, label: 'Division' },
];

/** Whether the type answers from a list of options. */
export function isChoiceType(type: SurveyQuestionTypeValue): boolean {
  return (
    type === SURVEY_QUESTION_TYPE.MultipleChoice ||
    type === SURVEY_QUESTION_TYPE.Checkbox ||
    type === SURVEY_QUESTION_TYPE.Dropdown
  );
}

/** One questionnaire of a survey — its basic information. */
export interface SurveyQuestionnaire {
  id: string;
  surveyId: string;
  title: string;
  description: string | null;
  /** How many live questions it has. */
  questionCount: number;
  /** How many live responses it has. While any exists, its questions are locked. */
  responseCount: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface SurveyQuestionnaireOption {
  id: string;
  text: string;
}

export interface SurveyQuestionnaireQuestion {
  id: string;
  text: string;
  /** The note under the question. */
  description: string | null;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel: string | null;
  scaleMaxLabel: string | null;
  options: SurveyQuestionnaireOption[];
}

/** One questionnaire with its live questions — what the edit form opens on. */
export interface SurveyQuestionnaireDetail {
  questionnaire: SurveyQuestionnaire;
  questions: SurveyQuestionnaireQuestion[];
}

export interface SurveyQuestionnaireQuestionInput {
  /** Null for a question this form added. */
  id: string | null;
  text: string;
  description: string | null;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel: string | null;
  scaleMaxLabel: string | null;
  /** A null id is a new option; a blank one is dropped by the server. */
  options: { id: string | null; text: string }[];
}

/** The whole questionnaire, questions included, in one request. */
export interface SurveyQuestionnaireRequest {
  title: string;
  description: string | null;
  questions: SurveyQuestionnaireQuestionInput[];
}

/**
 * One row of a survey's Responses › Assessment.
 *
 * **No name.** `personId` is what the Name cell resolves, in one batch, through the shared
 * `usePersonNames`. It is ABSENT for an employee above the reader's access level — the row is
 * still listed and counted, and the cell says Restricted.
 */
export interface SurveyRespondent {
  id: string;
  personId?: string | null;
  isRestricted: boolean;
  employeeNumber: string;
  /** The employee's CURRENT unit, live from their primary post. */
  orgUnitName: string | null;
  remarks: string | null;
  respondedAt: string;
  /** When it was assessed — marked ready for a development plan. Absent until then. */
  assessedAt?: string | null;
  assessedByName?: string | null;
  assessmentRemarks?: string | null;
  /** When the plan was first written. Absent until then. */
  developmentPlanAt?: string | null;
  developmentPlanByName?: string | null;
  /** Where the plan stands with its approver — `SURVEY_PLAN_STATUS`, numeric on the wire. */
  developmentPlanStatus: SurveyPlanStatusValue;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** One answer as the respondent's form sends it. Only the value the question's type uses is read. */
export interface SurveyAnswerInput {
  questionId: string;
  optionIds: string[];
  text: string | null;
  number: number | null;
  /** `YYYY-MM-DD` — a calendar date, never an instant. */
  date: string | null;
  /** `HH:mm`. */
  time: string | null;
  /** Division: the unit chosen. */
  orgUnitId: string | null;
  /** Division: a unit beneath it, when it has any. */
  sectorOrgUnitId: string | null;
}

export interface SubmitSurveyResponseRequest {
  /** Answering on behalf: the employee picked on the Name question. Needs `surveys.respond-on-behalf`. */
  respondentPersonnelId: string | null;
  remarks: string | null;
  answers: SurveyAnswerInput[];
}

/** One answer as it was given. Nullable wire fields may arrive as undefined. */
export interface SurveyAnswer {
  questionId: string;
  optionIds: string[];
  text?: string | null;
  number?: number | null;
  date?: string | null;
  time?: string | null;
  orgUnitId?: string | null;
  sectorOrgUnitId?: string | null;
}

export interface MySurveyResponse {
  respondedAt: string;
  remarks?: string | null;
  answers: SurveyAnswer[];
}

/** What the answer form opens on. */
export interface SurveyResponseState {
  /** The account is linked to a live personnel record — answering requires it. */
  isEmployee: boolean;
  /** Absent until the caller has answered. */
  response?: MySurveyResponse | null;
  /** The caller's own display name, for a Name question. Resolved, never stored. */
  myName?: string | null;
  /** The caller's CURRENT unit, which a Division question defaults to. */
  myOrgUnitId?: string | null;
}

/** One question of a response, with the answer given — already in words. Nullable fields may arrive as undefined. */
export interface SurveyResponseViewQuestion {
  questionId: string;
  text: string;
  description?: string | null;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel?: string | null;
  scaleMaxLabel?: string | null;
  answered: boolean;
  optionTexts: string[];
  answerText?: string | null;
  number?: number | null;
  /** `YYYY-MM-DD` — a calendar date. */
  date?: string | null;
  /** `HH:mm:ss`. */
  time?: string | null;
  orgUnitName?: string | null;
  sectorName?: string | null;
}

/** One employee's response, as an administrator reads it. No name — the list row resolved it. */
export interface SurveyResponseView {
  id: string;
  questionnaireTitle: string;
  questionnaireDescription?: string | null;
  respondedAt: string;
  remarks?: string | null;
  isActive: boolean;
  /** When it was assessed. Absent until then. */
  assessedAt?: string | null;
  assessedByName?: string | null;
  assessmentRemarks?: string | null;
  /** The development plan as SANITISED markup — rendered only through `<RichTextDocument>`. */
  developmentPlan?: string | null;
  /** When the plan was first written. Absent until then. */
  developmentPlanAt?: string | null;
  developmentPlanByName?: string | null;
  /** Where the plan stands with its approver — `SURVEY_PLAN_STATUS`, numeric on the wire. */
  developmentPlanStatus: SurveyPlanStatusValue;
  /** Whether approval sends the plan to the employee on its own — the plan form's tick. */
  developmentPlanAutoSend?: boolean;
  /** When the approved plan was sent to the employee. Absent until then. */
  developmentPlanSentAt?: string | null;
  /** When the EMPLOYEE accepted or declined the approved plan. */
  developmentPlanEmployeeDecisionAt?: string | null;
  /** Why the employee declined — what the revision answers. */
  developmentPlanDeclineReason?: string | null;
  /** The identified development needs, live, in order. */
  developmentNeeds: SurveyDevelopmentNeed[];
  questions: SurveyResponseViewQuestion[];
}

/**
 * One identified development need. The levels are the 201 file's own ladder —
 * `CompetencyLevel` from personnelTypes — numeric on the wire.
 */
export interface SurveyDevelopmentNeed {
  id: string;
  competencyId: string;
  /** The competency's LIVE name. */
  competencyName: string;
  currentLevel: CompetencyLevel;
  targetLevel: CompetencyLevel;
  gap?: string | null;
  /** The level REACHED after the training — the impact assessment's. Absent until it is made. */
  achievedLevel?: CompetencyLevel | null;
}

/** The impact assessment as its form sends it: the level reached on EVERY live need, and remarks. */
export interface SaveImpactAssessmentRequest {
  needs: { needId: string; achievedLevel: CompetencyLevel }[];
  remarks: string;
}

/** One need as the form sends it. A null id is new; a row with nothing in it is dropped by the server. */
export interface SurveyDevelopmentNeedInput {
  id: string | null;
  competencyId: string | null;
  currentLevel: CompetencyLevel | null;
  targetLevel: CompetencyLevel | null;
  gap: string | null;
}

/** The needs and the plan, saved together. */
export interface SaveDevelopmentPlanRequest {
  needs: SurveyDevelopmentNeedInput[];
  plan: string | null;
  /** Whether approval sends the plan to the employee on its own. Omitted leaves the stored choice alone. */
  autoSend?: boolean;
}

/** One bar of a summary chart. */
export interface SurveySummaryBar {
  label: string;
  count: number;
}

/**
 * One question of a questionnaire's summary. Counts are of every live response; `texts` are
 * written answers only from employees the reader is cleared for — the rest are `withheldCount`.
 * Date and Time texts arrive RAW (`yyyy-MM-dd`, `HH:mm`) for the browser to format.
 */
export interface SurveySummaryQuestion {
  questionId: string;
  text: string;
  description?: string | null;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  answeredCount: number;
  /** How many of the survey's TARGETED employees answered it — against the summary's `targetedCount`. */
  targetedAnsweredCount: number;
  bars: SurveySummaryBar[];
  texts: string[];
  withheldCount: number;
  /** Scale only: the mean rating, unrounded; absent when nobody has rated it (not 0). */
  average?: number | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
}

export interface SurveyQuestionnaireSummary {
  responseCount: number;
  /** Live employees the survey's divisions and positions cover. */
  targetedCount: number;
  /** How many of those have answered. Never above the target. */
  targetedRespondedCount: number;
  /** Responses from employees outside the target — counted apart. */
  outsideTargetCount: number;
  questions: SurveySummaryQuestion[];
}

/** One respondent's answer to one question. No name; `personId` and `answer` are absent above the reader's level. */
export interface SurveyQuestionAnswerRow {
  respondentId: string;
  personId?: string | null;
  employeeNumber: string;
  isRestricted: boolean;
  respondedAt: string;
  answer?: string | null;
}

/**
 * Where a development plan stands with its approver — mirrors `SurveyDevelopmentPlanStatus`.
 * Moved by the approval engine's callbacks, never by an endpoint of this module.
 */
export const SURVEY_PLAN_STATUS = {
  NotSubmitted: 0,
  Submitted: 1,
  Approved: 2,
  Rejected: 3,
  /** The employee accepted the approved plan — final; locked for good. */
  Accepted: 4,
  /** The employee declined it, with a reason — it reopens for revision. */
  Declined: 5,
} as const;

export type SurveyPlanStatusValue = (typeof SURVEY_PLAN_STATUS)[keyof typeof SURVEY_PLAN_STATUS];

/** The plan's status in words — the engine's own: Approved, Rejected; "For approval" while it waits. */
export const SURVEY_PLAN_STATUS_LABELS: Record<SurveyPlanStatusValue, string> = {
  0: 'Not submitted',
  1: 'For approval',
  2: 'Approved',
  3: 'Rejected',
  4: 'Accepted',
  5: 'Declined',
};

/** Locked while an approver holds it and once approved — the server refuses a change either way. */
export function isPlanLocked(status: SurveyPlanStatusValue | null | undefined): boolean {
  return (
    status === SURVEY_PLAN_STATUS.Submitted ||
    status === SURVEY_PLAN_STATUS.Approved ||
    status === SURVEY_PLAN_STATUS.Accepted
  );
}

/**
 * One employee a plan was created for. No name: `personId` is resolved in one batch, and it and
 * the employee number are ABSENT above the reader's access level — the member is still listed.
 */
export interface SurveyPlanMember {
  /** The response holding this employee's own copy — also their own plan's profile id. */
  respondentId: string;
  personId?: string | null;
  isRestricted: boolean;
  employeeNumber?: string | null;
  /** Where this employee's own copy stands — `SURVEY_PLAN_STATUS`. */
  developmentPlanStatus: SurveyPlanStatusValue;
  isActive: boolean;
}

/** One row of Survey › Development Plan — a plan created once for several assessed employees. */
export interface SurveyPlan {
  id: string;
  surveyId: string;
  /** The survey's LIVE title — what a list spanning every survey says each plan came from. */
  surveyTitle: string;
  title: string;
  needCount: number;
  members: SurveyPlanMember[];
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/** A plan's profile: what was created, and for whom. */
export interface SurveyPlanDetail {
  plan: SurveyPlan;
  surveyTitle: string;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  document?: string | null;
  needs: SurveyDevelopmentNeed[];
}

/** Creating a plan: each chosen employee gets their own copy. */
export interface CreateSurveyPlanRequest {
  title: string;
  plan: string;
  /** The training recommendations written with the plan — the grid's rows; a blank one is dropped. */
  trainingRecommendations: SurveyTrainingRecommendationRequest[];
  needs: SurveyDevelopmentNeedInput[];
  respondentIds: string[];
  /** Employees BY PERSONNEL — anybody, whether or not they answered. What the form sends. */
  personnelIds: string[];
}

/** The backend's `SurveyTrainingRecommendationStatus` — numeric on the wire, moved only by its approval. */
export const TRAINING_RECOMMENDATION_STATUS = { NotSubmitted: 0, Submitted: 1, Approved: 2, Rejected: 3 } as const;
export type TrainingRecommendationStatusValue =
  (typeof TRAINING_RECOMMENDATION_STATUS)[keyof typeof TRAINING_RECOMMENDATION_STATUS];

export const TRAINING_RECOMMENDATION_STATUS_LABELS: Record<TrainingRecommendationStatusValue, string> = {
  0: 'Not submitted',
  1: 'For approval',
  2: 'Approved',
  3: 'Rejected',
};

/** Locked while an approver holds it and once approved — the server refuses a change either way. */
export function isRecommendationLocked(status: TrainingRecommendationStatusValue): boolean {
  return status === TRAINING_RECOMMENDATION_STATUS.Submitted || status === TRAINING_RECOMMENDATION_STATUS.Approved;
}

/** One row of a development plan's Training Recommendations. */
export interface SurveyTrainingRecommendation {
  id: string;
  surveyPlanId: string;
  /** The plan it is written on, and the survey that plan is under — what the register files it by. */
  planTitle: string;
  surveyId: string;
  surveyTitle: string;
  title: string;
  status: TrainingRecommendationStatusValue;
  /** When the APPROVED recommendation was sent to the plan's employees — what lets them read it. */
  sentAt?: string | null;
  sentByName?: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/**
 * The recommendation's approval as its own record states it — the decision, and the request to
 * open. The chain (who signs, each decision, a refusal's reason) is the approval's own page.
 */
export interface SurveyTrainingRecommendationApproval {
  requestId: string;
  /** Pending, Approved, Rejected, Cancelled or Retracted — the engine's own words. */
  statusLabel: string;
  /** Whether the request is waiting on THIS reader's decision — the engine's answer, per caller. */
  canDecide: boolean;
  subjectReference?: string | null;
  stepCount: number;
  requireAll: boolean;
  submittedAt: string;
  submittedByName?: string | null;
  /** When the latest decision was made. Absent while nobody has decided. */
  decidedAt?: string | null;
  decidedByName?: string | null;
  /** What the approver wrote with it — optional on an approval, required on a decline. */
  remarks?: string | null;
}

/** A training recommendation's profile. */
export interface SurveyTrainingRecommendationDetail {
  recommendation: SurveyTrainingRecommendation;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  details?: string | null;
  planTitle: string;
  surveyId: string;
  surveyTitle: string;
  /** How many live employees are on its plan — who it is, or would be, sent to. */
  memberCount: number;
  /** Absent until it has been put to an approver. */
  approval?: SurveyTrainingRecommendationApproval | null;
}

export interface SurveyTrainingRecommendationRequest {
  title: string;
  details: string;
}

/** The backend's `SurveyTrainingDecision` — an employee's answer to a sent recommendation. */
export const TRAINING_DECISION = { Pending: 0, Accepted: 1, Declined: 2 } as const;
export type TrainingDecisionValue = (typeof TRAINING_DECISION)[keyof typeof TRAINING_DECISION];

export const TRAINING_DECISION_LABELS: Record<TrainingDecisionValue, string> = {
  0: 'Pending',
  1: 'Accepted',
  2: 'Declined',
};

/**
 * One employee on a training recommendation's Personnel table — a member of its plan, with their
 * own track on it once it was sent. No name: `personId` and the employee number are ABSENT above
 * the reader's access level, where the row is still listed.
 */
export interface SurveyTrainingRecommendationPersonnelRow {
  respondentId: string;
  personId?: string | null;
  isRestricted: boolean;
  employeeNumber?: string | null;
  /** Whether the recommendation has reached them. False until it is sent. */
  isSent: boolean;
  decision: TrainingDecisionValue;
  decidedAt?: string | null;
  /** When they enrolled — an instant. Absent until they do. */
  enrolledAt?: string | null;
  /** Calendar dates, `YYYY-MM-DD` — format with `formatDateOnly`. */
  trainingStartDate?: string | null;
  trainingEndDate?: string | null;
  trainingCompletedAt?: string | null;
  /** How the training ended — 0 Completed, 1 Cancelled. Absent while it has not, and on an earlier completion. */
  trainingResult?: 0 | 1 | null;
  reportAt?: string | null;
  /** Whether their own plan holds a development need — what the impact's level is given against. */
  hasNeed: boolean;
  /** The impact assessment of this training on them. Absent until it is made. Wire-numeric level. */
  /** Passed or failed — absent until the impact is assessed. */
  impactPassed?: boolean | null;
  impactAchievedLevel?: number | null;
  impactAssessmentAt?: string | null;
}

/** One employee's whole track on a recommendation, as Learning & Development reads it. */
export interface SurveyTrainingRecommendationTrack {
  respondentId: string;
  personId?: string | null;
  employeeNumber?: string | null;
  decision: TrainingDecisionValue;
  decidedAt?: string | null;
  declineReason?: string | null;
  /** When the employee enrolled — they named the school and its location. */
  enrolledAt?: string | null;
  /** The training school the employee named. */
  trainingSchool?: string | null;
  /** The training's location, rung by rung — write it with `addressPathLabel`. */
  trainingProvince?: string | null;
  trainingCityMunicipality?: string | null;
  trainingBarangay?: string | null;
  trainingStreetAddress?: string | null;
  /** The training duration given at enrollment — calendar dates. */
  enrollmentStartDate?: string | null;
  enrollmentEndDate?: string | null;
  trainingStartDate?: string | null;
  trainingEndDate?: string | null;
  trainingRecordedAt?: string | null;
  trainingRemarks?: string | null;
  trainingCompletedAt?: string | null;
  /** How the training ended — 0 Completed, 1 Cancelled. Absent while it has not, and on an earlier completion. */
  trainingResult?: 0 | 1 | null;
  trainingCompletionRemarks?: string | null;
  reportAt?: string | null;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  report?: string | null;
  reportAttachments: { id: string; fileName: string }[];
  impactPassed?: boolean | null;
  impactAchievedLevel?: number | null;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  impactAssessment?: string | null;
  impactAssessmentAt?: string | null;
  impactAssessmentByName?: string | null;
}

/** One employee's impact assessment on a training recommendation, as its form sends it. */
export interface TrainingImpactAssessmentInput {
  respondentId: string;
  /** The level reached — owed where the employee's plan holds a need; never Not Rated. */
  achievedLevel: number | null;
  /** Passed or failed — always answered; the server refuses an omitted one. */
  passed: boolean;
  /** The assessment — a rich-text document, sanitised on write. */
  assessment: string;
}
